import React from 'react';
import { useNavigate } from 'react-router-dom';
import './LegalPage.css';

const LegalPage = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      {/* Header */}
      <header className="legal-header">
        <div className="header-content">
          <div className="logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <span className="logo-icon">🌍</span>
            <h1>Teacha</h1>
          </div>
          <button className="btn-back" onClick={() => navigate('/')}>
            ← Back to Home
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="legal-content">
        <h1>Terms of Service & Legal Information</h1>
        <p className="last-updated">Last Updated: November 2, 2025</p>

        {/* Introduction */}
        <section className="legal-section">
          <h2>1. Introduction</h2>
          <p>
            Welcome to Teacha. By accessing or using our platform, you agree to be bound by these Terms of Service ("Terms").
            If you do not agree to these Terms, please do not use our services.
          </p>
          <p>
            Teacha provides a language learning platform connecting students with native speaker teachers through
            video conferencing, AI-powered flashcard generation, and progress tracking tools.
          </p>
        </section>

        {/* Free Trial Terms */}
        <section className="legal-section">
          <h2>2. Free Trial & Subscription</h2>

          <h3>2.1 Student Free Trial</h3>
          <p>
            New student users receive a 7-day free trial period. During this trial, you have full access to all
            premium features including:
          </p>
          <ul>
            <li>Unlimited 1:1 video sessions</li>
            <li>Automatic flashcard generation</li>
            <li>Mobile and desktop app access</li>
            <li>Progress tracking and analytics</li>
            <li>Spaced repetition learning system</li>
          </ul>

          <h3>2.2 Subscription Billing</h3>
          <p>
            After the 7-day trial period, your subscription will automatically renew at $2.99/month unless you cancel.
            Subscriptions are managed through the Google Play Store (Android) or Apple App Store (iOS). To cancel,
            please use your respective app store's subscription management features.
          </p>

          <h3>2.3 Trial Abuse Prevention</h3>
          <p>
            To prevent abuse of our free trial system:
          </p>
          <ul>
            <li>Each user is limited to one free trial</li>
            <li>Trial accounts require OAuth authentication (Google or Apple Sign-In)</li>
            <li>We monitor for fraudulent account creation patterns</li>
            <li>Accounts found to be abusing the trial system will be immediately terminated without refund</li>
          </ul>

          <h3>2.4 Teacher Accounts</h3>
          <p>
            Teacher accounts are always free. Teachers can join student video sessions as guests to help students
            practice conversation. Teachers do not have access to flashcard generation, progress tracking, or other
            student-specific features.
          </p>
        </section>

        {/* Acceptable Use Policy */}
        <section className="legal-section">
          <h2>3. Acceptable Use Policy</h2>

          <h3>3.1 Prohibited Activities</h3>
          <p>You agree NOT to use Teacha for any of the following:</p>
          <ul>
            <li>Harassment, abuse, or threatening behavior toward other users</li>
            <li>Sharing inappropriate, offensive, or illegal content during video sessions</li>
            <li>Attempting to circumvent payment systems or subscription verification</li>
            <li>Reverse engineering, decompiling, or attempting to extract source code</li>
            <li>Automated scraping, data mining, or bot usage</li>
            <li>Impersonating other users or providing false information</li>
            <li>Sharing account credentials with others</li>
            <li>Using the platform for commercial purposes without written permission</li>
          </ul>

          <h3>3.2 Video Conferencing Conduct</h3>
          <p>
            During video sessions, all participants must maintain respectful, professional conduct. Sessions are
            intended for language practice and educational purposes only. Any reports of inappropriate behavior
            will result in immediate account suspension pending investigation.
          </p>
        </section>

        {/* Liability & Disclaimers */}
        <section className="legal-section">
          <h2>4. Liability & Disclaimers</h2>

          <h3>4.1 Service Availability</h3>
          <p>
            Teacha is provided "AS IS" and "AS AVAILABLE" without warranties of any kind. We do not guarantee:
          </p>
          <ul>
            <li>Uninterrupted or error-free service</li>
            <li>Specific learning outcomes or language proficiency improvements</li>
            <li>Availability of specific teachers or session times</li>
            <li>Accuracy of AI-generated flashcards</li>
            <li>Data backup or recovery</li>
          </ul>

          <h3>4.2 Limitation of Liability</h3>
          <p>
            To the maximum extent permitted by law, Teacha and its affiliates shall not be liable for:
          </p>
          <ul>
            <li>Indirect, incidental, special, or consequential damages</li>
            <li>Loss of data, profits, or business opportunities</li>
            <li>Damages arising from third-party content or user conduct</li>
            <li>Service interruptions, technical failures, or data loss</li>
          </ul>
          <p>
            Our total liability shall not exceed the amount you paid in the past 12 months (maximum $35.88 for annual subscribers).
          </p>

          <h3>4.3 Teacher Relationships</h3>
          <p>
            Teacha is a platform facilitating connections between students and teachers. We do not:
          </p>
          <ul>
            <li>Employ, endorse, or verify the credentials of teachers</li>
            <li>Guarantee the quality, availability, or professionalism of teachers</li>
            <li>Assume liability for interactions between students and teachers outside the platform</li>
          </ul>
          <p>
            Students are responsible for vetting their own teachers and ensuring appropriate conduct during sessions.
          </p>
        </section>

        {/* Privacy & Data */}
        <section className="legal-section">
          <h2>5. Privacy & Data Protection</h2>

          <h3>5.1 Data Collection</h3>
          <p>We collect and process the following data:</p>
          <ul>
            <li><strong>Account Data:</strong> Email, name, OAuth provider ID (Google/Apple)</li>
            <li><strong>Usage Data:</strong> Session recordings, transcriptions, flashcard interactions</li>
            <li><strong>Analytics:</strong> Device information, app usage patterns, performance metrics</li>
            <li><strong>Payment Data:</strong> Handled securely through Google Play/Apple App Store (we do not store credit card numbers)</li>
          </ul>

          <h3>5.2 Video Recording & Transcription</h3>
          <p>
            All video sessions are automatically recorded and transcribed for the purpose of generating educational
            flashcards. By using Teacha:
          </p>
          <ul>
            <li>You consent to recording of your video sessions</li>
            <li>You acknowledge that recordings are processed by AI (Whisper transcription)</li>
            <li>You understand that transcriptions are stored indefinitely for flashcard generation</li>
            <li>You agree that recordings are for educational purposes only</li>
          </ul>

          <h3>5.3 Data Retention</h3>
          <ul>
            <li><strong>Active Accounts:</strong> Data retained indefinitely while account is active</li>
            <li><strong>Deleted Accounts:</strong> Data deleted within 30 days of account deletion request</li>
            <li><strong>Video Recordings:</strong> Original recordings deleted after 30 days; transcriptions retained</li>
            <li><strong>Backups:</strong> May persist in backups for up to 90 days</li>
          </ul>

          <h3>5.4 Data Sharing</h3>
          <p>We do NOT sell your personal data. We may share data with:</p>
          <ul>
            <li><strong>Service Providers:</strong> AWS (storage/transcription), Google Cloud (infrastructure)</li>
            <li><strong>Payment Processors:</strong> Google Play, Apple App Store</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
          </ul>
        </section>

        {/* Content & Intellectual Property */}
        <section className="legal-section">
          <h2>6. Content & Intellectual Property</h2>

          <h3>6.1 Your Content</h3>
          <p>
            You retain ownership of content you create (videos, flashcards, notes). By using Teacha, you grant us
            a worldwide, non-exclusive license to:
          </p>
          <ul>
            <li>Store and process your content for service delivery</li>
            <li>Generate flashcards from your conversation transcriptions</li>
            <li>Use anonymized, aggregated data for service improvements</li>
          </ul>

          <h3>6.2 Our Intellectual Property</h3>
          <p>
            Teacha's platform, including the website, mobile apps, AI models, algorithms, and branding, is owned by
            Teacha and protected by copyright, trademark, and other intellectual property laws. You may not:
          </p>
          <ul>
            <li>Copy, modify, or distribute our software or design</li>
            <li>Use our trademarks without permission</li>
            <li>Reverse engineer our AI models or algorithms</li>
          </ul>
        </section>

        {/* Account Termination */}
        <section className="legal-section">
          <h2>7. Account Termination</h2>

          <h3>7.1 Termination by You</h3>
          <p>
            You may cancel your account at any time through your app store subscription settings (Google Play/Apple).
            Cancellation takes effect at the end of your current billing period. No refunds for partial months.
          </p>

          <h3>7.2 Termination by Us</h3>
          <p>We reserve the right to suspend or terminate your account if:</p>
          <ul>
            <li>You violate these Terms of Service</li>
            <li>You engage in abusive or fraudulent behavior</li>
            <li>Your payment fails or is disputed</li>
            <li>We suspect illegal activity</li>
            <li>We discontinue the service (with 30 days notice)</li>
          </ul>
        </section>

        {/* Changes to Terms */}
        <section className="legal-section">
          <h2>8. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. If we make material changes, we will notify you via:
          </p>
          <ul>
            <li>Email to your registered address</li>
            <li>In-app notification</li>
            <li>Banner on the website</li>
          </ul>
          <p>
            Continued use of Teacha after changes take effect constitutes acceptance of the new Terms.
          </p>
        </section>

        {/* Governing Law */}
        <section className="legal-section">
          <h2>9. Governing Law & Dispute Resolution</h2>
          <p>
            These Terms are governed by the laws of the State of California, United States, without regard to
            conflict of law principles. Any disputes shall be resolved through binding arbitration in accordance
            with the American Arbitration Association rules.
          </p>
        </section>

        {/* Contact */}
        <section className="legal-section">
          <h2>10. Contact Information</h2>
          <p>
            For questions about these Terms, privacy concerns, or account issues, please contact us at:
          </p>
          <p>
            <strong>Email:</strong> legal@teacha.com<br />
            <strong>Support:</strong> support@teacha.com
          </p>
        </section>

        {/* Severability */}
        <section className="legal-section">
          <h2>11. Severability</h2>
          <p>
            If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions
            shall remain in full force and effect.
          </p>
        </section>

        {/* Entire Agreement */}
        <section className="legal-section">
          <h2>12. Entire Agreement</h2>
          <p>
            These Terms constitute the entire agreement between you and Teacha regarding use of the service,
            superseding any prior agreements.
          </p>
        </section>

        {/* Back to Top */}
        <div className="legal-footer">
          <button className="btn-primary" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            ↑ Back to Top
          </button>
          <button className="btn-secondary" onClick={() => navigate('/')}>
            Return to Home
          </button>
        </div>
      </main>
    </div>
  );
};

export default LegalPage;
